// Window-relative rects of tour targets. Several mounted views may share one id
// (e.g. the mountain frame and its placard); the spotlight is their union,
// counting only views that are currently on screen.
import type { View } from 'react-native';

export interface Rect { x: number; y: number; w: number; h: number }

const nodes = new Map<string, Set<View>>();

export function registerTarget(id: string, node: View): () => void {
  let set = nodes.get(id);
  if (!set) {
    set = new Set();
    nodes.set(id, set);
  }
  set.add(node);
  return () => {
    set?.delete(node);
    if (set && set.size === 0) nodes.delete(id);
  };
}

function measure(node: View): Promise<Rect | null> {
  return new Promise((resolve) => {
    try {
      node.measureInWindow((x, y, w, h) => {
        resolve(Number.isFinite(x) && Number.isFinite(y) && w > 0 && h > 0 ? { x, y, w, h } : null);
      });
    } catch {
      resolve(null);
    }
  });
}

/** Union of the on-screen rects registered under `id` (window coordinates), or null. */
export async function measureTarget(id: string, winW: number, winH: number): Promise<Rect | null> {
  const set = nodes.get(id);
  if (!set || set.size === 0) return null;
  const rects = (await Promise.all(Array.from(set).map(measure))).filter(
    (r): r is Rect => !!r && r.x < winW && r.y < winH && r.x + r.w > 0 && r.y + r.h > 0,
  );
  if (!rects.length) return null;
  const x0 = Math.min(...rects.map((r) => r.x));
  const y0 = Math.min(...rects.map((r) => r.y));
  const x1 = Math.max(...rects.map((r) => r.x + r.w));
  const y1 = Math.max(...rects.map((r) => r.y + r.h));
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}
